import { BED_JOINT_GAP_MM, DEFAULT_PERPEND_GAP_MM, EPSILON, SPATIAL_CELL_SIZE_MM } from './constants.js';
import type { BrickKind } from './catalogue.js';
import { brickSpec, effectiveSize, isBrickKind } from './catalogue.js';
import type { BrickId, PlacedBrick } from './brick.js';
import { DEFAULT_MATERIAL_ID, assertBrickId, createBrick, translateBrick, withMaterial } from './brick.js';
import { rectanglesHaveMortarClearance } from './clearance.js';
import { SpatialIndex } from './spatial-index.js';
import type { BrickPattern, PatternBrick } from './pattern.js';
import type { SnapRelation, SnapResult } from './snap.js';
import { SnapCandidateSet, SnapPriority, unsnapped } from './snap.js';
import type { Sketch, SketchSegment } from './sketch.js';
import {
  EMPTY_SKETCH,
  allSegments,
  closestPointOnSegment,
  isMoreVertical,
  xOnSegmentAtY,
  yOnSegmentAtX,
} from './sketch.js';
import type { MaterialId, Mm } from './units.js';
import { isQuantised, normaliseMaterialId, normalisePerpendGap, normaliseRotation, quantise } from './units.js';
import { fail } from './errors.js';

export interface Bounds {
  readonly minX: Mm;
  readonly minY: Mm;
  readonly maxX: Mm;
  readonly maxY: Mm;
}

/** The serialised shape of a brick (clause 5.4). */
export interface BrickRecord {
  readonly id: number;
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly rotation: number;
  readonly materialId: MaterialId;
}

/**
 * An undo snapshot: the brick list plus the wall-wide joint setting. Both
 * halves are needed, because undoing a joint change must restore the joint as
 * well as the coordinates (clause 5.4).
 *
 * Bricks are immutable, so this is an array of shared references rather than a
 * deep clone (design document D5).
 */
export interface WallSnapshot {
  readonly perpendGapMm: Mm;
  readonly bricks: readonly PlacedBrick[];
  /** The drawn outline is part of the wall, so undo must restore it too. */
  readonly sketch: Sketch;
}

export interface CornerSnap {
  readonly x: Mm;
  readonly y: Mm;
  /** Null when the point came from the sketch rather than a laid brick. */
  readonly brickId: BrickId | null;
  readonly corner: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' | 'sketch';
}

const CORNER_NAMES = ['bottom-left', 'bottom-right', 'top-left', 'top-right'] as const;

interface CompiledPattern {
  readonly entries: readonly PatternBrick[];
  /**
   * Present only when every offset sits exactly on the coordinate quantum and
   * the group has valid internal clearance. Rounding half-quantum offsets can
   * depend on the translated origin, so a pattern that is off-lattice keeps
   * the exact per-origin validation fallback (design document 8.2).
   */
  readonly relativeIndex: SpatialIndex | null;
  readonly internallyValid: boolean | null;
  readonly bounds: Bounds | null;
}

export interface AddBrickOptions {
  readonly rotation?: number;
  readonly materialId?: MaterialId;
  readonly brickId?: BrickId;
  /** Skip the clearance check. Only for callers that have already validated. */
  readonly validate?: boolean;
}

/**
 * Physical brick geometry in millimetres, independent of any renderer.
 * SPECIFICATION.md clauses 4 to 9.
 */
export class WallModel {
  #perpendGapMm: Mm;
  #bricks: PlacedBrick[] = [];
  #index = new SpatialIndex();
  #nextId = 1;
  #revision = 0;
  #patternCache = new WeakMap<BrickPattern, CompiledPattern | null>();
  #sketch: Sketch = EMPTY_SKETCH;
  #sketchSegments: readonly SketchSegment[] = [];

  constructor(bricks: Iterable<PlacedBrick> = [], perpendGapMm: number = DEFAULT_PERPEND_GAP_MM) {
    this.#perpendGapMm = normalisePerpendGap(perpendGapMm);
    for (const brick of bricks) {
      if (this.#index.has(brick.id)) fail(`Duplicate brick ID: ${brick.id}`);
      this.#bricks.push(brick);
      this.#index.add(brick);
      this.#nextId = Math.max(this.#nextId, brick.id + 1);
    }
  }

  // ---------- accessors ----------

  get perpendGapMm(): Mm {
    return this.#perpendGapMm;
  }

  get bedGapMm(): Mm {
    return BED_JOINT_GAP_MM;
  }

  /**
   * The drawn outline. It is part of the wall's geometry context, like the
   * perpend gap, so snapping picks it up without every call site growing a
   * parameter.
   */
  get sketch(): Sketch {
    return this.#sketch;
  }

  setSketch(sketch: Sketch): void {
    this.#sketch = sketch;
    this.#sketchSegments = allSegments(sketch);
    this.#touch();
  }

  /** Bricks in list order — the order used for iteration and serialisation. */
  get bricks(): readonly PlacedBrick[] {
    return this.#bricks;
  }

  get size(): number {
    return this.#bricks.length;
  }

  /**
   * Monotonic change counter. Replaces the specification's O(n) fingerprint
   * for dirty tracking, and is what React subscribes to (design document B3).
   */
  get revision(): number {
    return this.#revision;
  }

  get(id: BrickId | null | undefined): PlacedBrick | undefined {
    return id == null ? undefined : this.#index.get(id);
  }

  #touch(): void {
    this.#revision += 1;
    this.#patternCache = new WeakMap();
  }

  // ---------- mutation ----------

  clear(): void {
    this.#bricks = [];
    this.#index.clear();
    this.#nextId = 1;
    this.#touch();
  }

  addBrick(kind: string, x: number, y: number, options: AddBrickOptions = {}): PlacedBrick {
    if (!isBrickKind(kind)) fail(`Unknown brick type: ${kind}`);
    const rotation = normaliseRotation(options.rotation ?? 0);
    const materialId = normaliseMaterialId(options.materialId ?? DEFAULT_MATERIAL_ID);
    const qx = quantise(x);
    const qy = quantise(y);
    const [width, height] = effectiveSize(kind, rotation);
    if (options.validate !== false && !this.canPlace(qx, qy, width, height)) {
      fail('Brick overlaps another brick or has insufficient perpend/bed joint clearance');
    }
    const brickId = options.brickId ?? this.#nextId;
    assertBrickId(brickId);
    if (this.#index.has(brickId)) fail(`Duplicate brick ID: ${brickId}`);
    const brick = createBrick({ id: brickId, kind, x: qx, y: qy, rotation, materialId });
    this.#bricks.push(brick);
    this.#index.add(brick);
    this.#nextId = Math.max(this.#nextId, brickId + 1);
    this.#touch();
    return brick;
  }

  remove(id: BrickId): boolean {
    const brick = this.#index.get(id);
    if (brick === undefined) return false;
    this.#bricks.splice(this.#bricks.indexOf(brick), 1);
    this.#index.remove(id);
    this.#touch();
    return true;
  }

  /** Remove a group in one pass rather than shifting the list n times. */
  removeMany(ids: Iterable<BrickId>): number {
    const wanted = new Set(ids);
    if (wanted.size === 0) return 0;
    const survivors: PlacedBrick[] = [];
    let removed = 0;
    for (const brick of this.#bricks) {
      if (wanted.has(brick.id)) {
        this.#index.remove(brick.id);
        removed += 1;
      } else {
        survivors.push(brick);
      }
    }
    if (removed === 0) return 0;
    this.#bricks = survivors;
    this.#touch();
    return removed;
  }

  /**
   * Translate a group, preserving ids, kinds, rotations and materials — this
   * is a MOVE, not a delete-and-re-lay (design document C3).
   *
   * Because both the offset and the existing coordinates are on the 0.125 mm
   * lattice, the sum is exact: the group's internal geometry is carried over
   * untouched, so only its relationship to the rest of the wall needs testing.
   */
  canMoveBricks(ids: Iterable<BrickId>, dx: Mm, dy: Mm): boolean {
    const wanted = new Set(ids);
    if (wanted.size === 0) return false;
    const offsetX = quantise(dx);
    const offsetY = quantise(dy);
    for (const id of wanted) {
      const brick = this.#index.get(id);
      if (brick === undefined) return false;
      if (!this.canPlace(brick.x + offsetX, brick.y + offsetY, brick.width, brick.height, wanted)) {
        return false;
      }
    }
    return true;
  }

  /** Returns false when the offset is zero; throws when it would clash. */
  moveBricks(ids: Iterable<BrickId>, dx: Mm, dy: Mm): boolean {
    const wanted = new Set(ids);
    if (wanted.size === 0) return false;
    const offsetX = quantise(dx);
    const offsetY = quantise(dy);
    if (offsetX === 0 && offsetY === 0) return false;
    for (const id of wanted) {
      if (this.#index.get(id) === undefined) {
        fail('The selection contains a brick that is no longer in the wall');
      }
    }
    if (!this.canMoveBricks(wanted, offsetX, offsetY)) {
      fail('The move needs a full perpend and bed joint against every neighbour');
    }
    // Replace in place so the wall's list order — and therefore the file — is
    // unchanged by a move.
    for (let index = 0; index < this.#bricks.length; index++) {
      const brick = this.#bricks[index] as PlacedBrick;
      if (!wanted.has(brick.id)) continue;
      const moved = translateBrick(brick, offsetX, offsetY);
      this.#bricks[index] = moved;
      this.#index.remove(brick.id);
      this.#index.add(moved);
    }
    this.#touch();
    return true;
  }

  /** Assign a material. No-ops when the brick is missing or already matches. */
  setMaterial(id: BrickId, materialId: MaterialId): boolean {
    normaliseMaterialId(materialId);
    const brick = this.#index.get(id);
    if (brick === undefined || brick.materialId === materialId) return false;
    const replacement = withMaterial(brick, materialId);
    this.#bricks[this.#bricks.indexOf(brick)] = replacement;
    this.#index.remove(id);
    this.#index.add(replacement);
    this.#touch();
    return true;
  }

  /** Replace all geometry with an already validated model. */
  #adopt(other: WallModel): void {
    this.#perpendGapMm = other.#perpendGapMm;
    this.#sketch = other.#sketch;
    this.#sketchSegments = other.#sketchSegments;
    this.#bricks = other.#bricks;
    this.#index = other.#index;
    this.#nextId = other.#nextId;
    this.#touch();
  }

  // ---------- queries ----------

  /** The topmost brick containing a world point; last inserted wins. */
  hitTest(x: Mm, y: Mm): PlacedBrick | undefined {
    const candidates = this.#index.intersecting(x, y, x, y);
    for (let i = candidates.length - 1; i >= 0; i--) {
      const brick = candidates[i] as PlacedBrick;
      if (
        brick.x - EPSILON <= x &&
        x <= brick.right + EPSILON &&
        brick.y - EPSILON <= y &&
        y <= brick.top + EPSILON
      ) {
        return brick;
      }
    }
    return undefined;
  }

  /** Bricks touched by a rectangle, given in any corner order. */
  bricksInRect(x1: Mm, y1: Mm, x2: Mm, y2: Mm): PlacedBrick[] {
    return this.#index.intersecting(x1, y1, x2, y2);
  }

  bounds(): Bounds | null {
    if (this.#bricks.length === 0) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const brick of this.#bricks) {
      if (brick.x < minX) minX = brick.x;
      if (brick.y < minY) minY = brick.y;
      if (brick.right > maxX) maxX = brick.right;
      if (brick.top > maxY) maxY = brick.top;
    }
    return { minX, minY, maxX, maxY };
  }

  boundsForIds(ids: Iterable<BrickId>): Bounds | null {
    const wanted = new Set(ids);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let found = false;
    for (const brick of this.#bricks) {
      if (!wanted.has(brick.id)) continue;
      found = true;
      if (brick.x < minX) minX = brick.x;
      if (brick.y < minY) minY = brick.y;
      if (brick.right > maxX) maxX = brick.right;
      if (brick.top > maxY) maxY = brick.top;
    }
    return found ? { minX, minY, maxX, maxY } : null;
  }

  // ---------- clearance ----------

  /**
   * Clause 4.3. The query rectangle is expanded by the perpend gap on X and
   * the bed gap on Y — exactly wide enough that no brick outside it can be in
   * conflict.
   *
   * `exclude` carries the bricks being moved, so a drag can be validated
   * against the wall it is leaving (design document C3).
   */
  canPlace(
    x: Mm,
    y: Mm,
    width: Mm,
    height: Mm,
    exclude?: ReadonlySet<BrickId> | null,
  ): boolean {
    const perpend = this.#perpendGapMm;
    const nearby = this.#index.intersectingUnordered(
      x - perpend,
      y - BED_JOINT_GAP_MM,
      x + width + perpend,
      y + height + BED_JOINT_GAP_MM,
    );
    const candidate = { x, y, width, height };
    for (const brick of nearby) {
      if (exclude?.has(brick.id)) continue;
      if (!rectanglesHaveMortarClearance(candidate, brick, {
        perpendGapMm: perpend,
        bedGapMm: BED_JOINT_GAP_MM,
      })) {
        return false;
      }
    }
    return true;
  }

  // ---------- single-brick snapping ----------

  /**
   * Clause 6.3 and 6.4. Generate candidates by priority, sort, and return the
   * first that is actually placeable — so a blocked first choice degrades to
   * the next intent rather than to no snap at all.
   */
  snapPosition(
    rawX: number,
    rawY: number,
    width: Mm,
    height: Mm,
    toleranceMm: number,
    exclude?: ReadonlySet<BrickId> | null,
  ): SnapResult {
    const x = quantise(rawX);
    const y = quantise(rawY);
    const tolerance = Math.max(0, toleranceMm);
    const perpend = this.#perpendGapMm;
    const candidates = new SnapCandidateSet();
    let order = 0;

    const add = (
      cx: number,
      cy: number,
      priority: SnapPriority,
      relation: SnapRelation,
      referenceId: BrickId | null,
      snappedX: boolean,
      snappedY: boolean,
    ): void => {
      candidates.add({
        x: quantise(cx),
        y: quantise(cy),
        distance: Math.hypot(cx - x, cy - y),
        priority,
        order: order++,
        relation,
        referenceId,
        snappedX,
        snappedY,
      });
    };

    const paddingX = perpend + tolerance;
    const paddingY = BED_JOINT_GAP_MM + tolerance;
    const nearby = this.#index.intersecting(
      x - paddingX,
      y - paddingY,
      x + width + paddingX,
      y + height + paddingY,
    );

    for (const brick of nearby) {
      if (exclude?.has(brick.id)) continue;

      // Priority 0 — end joints. Both axes must land inside tolerance.
      // Offering a shared baseline AND a shared top edge is what lets
      // soldiers sit correctly against ordinary stretchers.
      const sides: readonly (readonly [number, SnapRelation])[] = [
        [brick.right + perpend, 'right'],
        [brick.x - perpend - width, 'left'],
      ];
      const verticalAlignments = [brick.y, brick.top - height];
      for (const [targetX, relation] of sides) {
        if (Math.abs(x - targetX) > tolerance + EPSILON) continue;
        for (const targetY of verticalAlignments) {
          if (Math.abs(y - targetY) <= tolerance + EPSILON) {
            add(targetX, targetY, SnapPriority.EndJoint, relation, brick.id, true, true);
          }
        }
      }

      // Priority 1 — bed joints, only when strictly overlapping in X.
      const overlapsHorizontally = x < brick.right - EPSILON && x + width > brick.x + EPSILON;
      if (!overlapsHorizontally) continue;
      const beds: readonly (readonly [number, SnapRelation])[] = [
        [brick.top + BED_JOINT_GAP_MM, 'above'],
        [brick.y - BED_JOINT_GAP_MM - height, 'below'],
      ];
      for (const [targetY, relation] of beds) {
        if (Math.abs(y - targetY) > tolerance + EPSILON) continue;
        // The last two put either end of the new brick's perpend joint on the
        // supporting brick's centreline. At the default gap a 215 mm stretcher
        // lands on the coordinated half-bond positions, +/- 112.5 mm.
        const alignments = [
          brick.x,
          brick.right - width,
          brick.centreX - width / 2,
          brick.centreX + perpend / 2,
          brick.centreX - perpend / 2 - width,
        ];
        const targetX = pickNearest(alignments, x);
        // The tighter sub-tolerance stops a bed snap from also yanking the
        // brick sideways when a free horizontal position was clearly meant.
        const snapX = Math.abs(targetX - x) <= tolerance * 0.6 + EPSILON;
        add(snapX ? targetX : x, targetY, SnapPriority.BedJoint, relation, brick.id, snapX, true);
      }
    }

    // Sketch lines. A brick edge can land on any drawn line, wall or
    // construction alike — a construction line is fainter, not weaker.
    for (const target of this.#sketchTargets(x, y, width, height, tolerance)) {
      add(target.x, target.y, SnapPriority.SketchLine, 'sketch', null, target.snappedX, target.snappedY);
    }

    // Datum snaps. The Y-axis accepts a left edge, centreline or right edge.
    const baselineClose = Math.abs(y) <= tolerance + EPSILON;
    for (const targetX of [0, -width / 2, -width]) {
      if (Math.abs(x - targetX) > tolerance + EPSILON) continue;
      if (baselineClose) add(targetX, 0, SnapPriority.BothAxes, 'axes', null, true, true);
      add(targetX, y, SnapPriority.OneAxis, 'y-axis', null, true, false);
    }
    if (baselineClose) add(x, 0, SnapPriority.OneAxis, 'baseline', null, false, true);

    for (const candidate of candidates.sorted()) {
      if (this.canPlace(candidate.x, candidate.y, width, height, exclude)) {
        return {
          x: candidate.x,
          y: candidate.y,
          relation: candidate.relation,
          referenceId: candidate.referenceId,
          snappedX: candidate.snappedX,
          snappedY: candidate.snappedY,
        };
      }
    }
    return unsnapped(x, y);
  }

  /**
   * Positions that seat a `width` x `height` footprint against a drawn line,
   * at ANY orientation.
   *
   * Every corner can be put on the line, which covers diagonals; and the
   * footprint can hold one axis to the line and slide on the other, which is
   * how a wall line is normally worked to.
   */
  #sketchTargets(
    x: Mm,
    y: Mm,
    width: Mm,
    height: Mm,
    tolerance: number,
  ): { x: Mm; y: Mm; snappedX: boolean; snappedY: boolean }[] {
    if (this.#sketchSegments.length === 0 || tolerance <= 0) return [];
    const out: { x: Mm; y: Mm; snappedX: boolean; snappedY: boolean }[] = [];
    const within = (targetX: number, targetY: number): boolean =>
      Math.abs(targetX - x) <= tolerance + EPSILON && Math.abs(targetY - y) <= tolerance + EPSILON;
    const push = (targetX: number, targetY: number, snappedX: boolean, snappedY: boolean): void => {
      if (within(targetX, targetY)) out.push({ x: targetX, y: targetY, snappedX, snappedY });
    };

    const corners: readonly (readonly [Mm, Mm])[] = [
      [0, 0],
      [width, 0],
      [0, height],
      [width, height],
    ];

    for (const { a, b } of this.#sketchSegments) {
      // Cheap reject: a segment further than the tolerance from the footprint
      // in either axis cannot offer anything.
      if (
        Math.min(a.x, b.x) > x + width + tolerance ||
        Math.max(a.x, b.x) < x - tolerance ||
        Math.min(a.y, b.y) > y + height + tolerance ||
        Math.max(a.y, b.y) < y - tolerance
      ) {
        continue;
      }

      // Oblique lines: put a CORNER on the line. This is what makes a gable
      // slope or a raking cut snappable at all, not only at its endpoints.
      //
      // Restricted to oblique segments on purpose. Projecting a corner onto an
      // axis-aligned segment constrains ONE axis, so reporting it as a
      // both-axis snap would let it outrank the endpoint that genuinely seats
      // the brick into the corner.
      const axisAligned =
        Math.abs(a.x - b.x) <= EPSILON || Math.abs(a.y - b.y) <= EPSILON;
      if (!axisAligned) {
        for (const [cx, cy] of corners) {
          const nearest = closestPointOnSegment(a, b, x + cx, y + cy);
          push(nearest.x - cx, nearest.y - cy, true, true);
        }
      }

      // Endpoints seat a corner exactly into a corner. Projection onto one
      // segment at a time can never land on the meeting of two, so this is not
      // covered by the corner-on-line case above.
      for (const endpoint of [a, b]) {
        for (const [cx, cy] of corners) {
          push(endpoint.x - cx, endpoint.y - cy, true, true);
        }
      }

      // Along the line: hold one axis and slide freely on the other, which is
      // how a wall line is normally worked to. Solved at the footprint's mid
      // height or width, so a line that is a millimetre off square still
      // behaves like the wall the user meant to draw.
      if (isMoreVertical(a, b)) {
        const lineX = xOnSegmentAtY(a, b, y + height / 2);
        if (lineX !== null) {
          push(lineX, y, true, false);
          push(lineX - width, y, true, false);
        }
      } else {
        const lineY = yOnSegmentAtX(a, b, x + width / 2);
        if (lineY !== null) {
          push(x, lineY, false, true);
          push(x, lineY - height, false, true);
        }
      }
    }
    return out;
  }

  // ---------- patterns ----------

  /** Copy selected bricks in wall order, relative to a lower-left origin. */
  patternFromIds(ids: Iterable<BrickId>): BrickPattern {
    const wanted = new Set(ids);
    if (wanted.size === 0) fail('Select at least one brick to copy');
    const bricks = this.#bricks.filter((brick) => wanted.has(brick.id));
    if (bricks.length !== wanted.size) {
      fail('The selection contains a brick that is no longer in the wall');
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const brick of bricks) {
      if (brick.x < minX) minX = brick.x;
      if (brick.y < minY) minY = brick.y;
      if (brick.right > maxX) maxX = brick.right;
      if (brick.top > maxY) maxY = brick.top;
    }
    const entries: PatternBrick[] = bricks.map((brick) => ({
      kind: brick.kind,
      dx: quantise(brick.x - minX),
      dy: quantise(brick.y - minY),
      rotation: brick.rotation,
      materialId: brick.materialId,
      width: brick.width,
      height: brick.height,
    }));
    const pattern: BrickPattern = {
      bricks: entries,
      width: quantise(maxX - minX),
      height: quantise(maxY - minY),
    };
    // Copying is a discrete action; compile the reusable geometry here so
    // subsequent paste-preview motion stays lightweight (clause 8.2).
    this.#compilePattern(pattern);
    return pattern;
  }

  #compilePattern(pattern: BrickPattern): CompiledPattern | null {
    const cached = this.#patternCache.get(pattern);
    if (cached !== undefined) return cached;

    let offsetsAreQuantised = true;
    const entries: PatternBrick[] = [];
    try {
      for (const entry of pattern.bricks) {
        if (!isBrickKind(entry.kind)) {
          this.#patternCache.set(pattern, null);
          return null;
        }
        normaliseMaterialId(entry.materialId);
        const rotation = normaliseRotation(entry.rotation);
        const [width, height] = effectiveSize(entry.kind, rotation);
        if (![entry.dx, entry.dy, width, height].every(Number.isFinite)) {
          this.#patternCache.set(pattern, null);
          return null;
        }
        offsetsAreQuantised &&= isQuantised(entry.dx) && isQuantised(entry.dy);
        entries.push({ ...entry, rotation, width, height });
      }
    } catch {
      this.#patternCache.set(pattern, null);
      return null;
    }

    let compiled: CompiledPattern;
    if (entries.length === 0) {
      compiled = { entries: [], relativeIndex: null, internallyValid: false, bounds: null };
    } else if (!offsetsAreQuantised) {
      compiled = { entries, relativeIndex: null, internallyValid: null, bounds: null };
    } else {
      const relative = new WallModel([], this.#perpendGapMm);
      let internallyValid = true;
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i] as PatternBrick;
        if (!relative.canPlace(entry.dx, entry.dy, entry.width, entry.height)) {
          internallyValid = false;
          break;
        }
        // Ids are 1..n in entry order, so a relative-index hit maps straight
        // back to its entry via `id - 1`.
        relative.addBrick(entry.kind, entry.dx, entry.dy, {
          rotation: entry.rotation,
          materialId: entry.materialId,
          brickId: i + 1,
          validate: false,
        });
      }
      compiled = internallyValid
        ? { entries, relativeIndex: relative.#index, internallyValid: true, bounds: relative.bounds() }
        : { entries, relativeIndex: null, internallyValid: false, bounds: null };
    }
    this.#patternCache.set(pattern, compiled);
    return compiled;
  }

  /** Validate every member of a translated pattern without mutating the wall. */
  canPlacePattern(
    pattern: BrickPattern,
    originX: number,
    originY: number,
    exclude?: ReadonlySet<BrickId> | null,
  ): boolean {
    const compiled = this.#compilePattern(pattern);
    if (compiled === null || compiled.entries.length === 0 || compiled.internallyValid === false) {
      return false;
    }
    let ox: number;
    let oy: number;
    try {
      ox = quantise(originX);
      oy = quantise(originY);
    } catch {
      return false;
    }

    const perpend = this.#perpendGapMm;
    if (compiled.relativeIndex !== null && compiled.bounds !== null) {
      // Fast path: query the wall near the translated group, then for each
      // hit query the relative index for the members actually adjacent to it.
      const b = compiled.bounds;
      const nearbyWall = this.#index.intersectingUnordered(
        ox + b.minX - perpend,
        oy + b.minY - BED_JOINT_GAP_MM,
        ox + b.maxX + perpend,
        oy + b.maxY + BED_JOINT_GAP_MM,
      );
      for (const wallBrick of nearbyWall) {
        if (exclude?.has(wallBrick.id)) continue;
        const members = compiled.relativeIndex.intersectingUnordered(
          wallBrick.x - ox - perpend,
          wallBrick.y - oy - BED_JOINT_GAP_MM,
          wallBrick.right - ox + perpend,
          wallBrick.top - oy + BED_JOINT_GAP_MM,
        );
        for (const member of members) {
          if (!rectanglesHaveMortarClearance(
            { x: ox + member.x, y: oy + member.y, width: member.width, height: member.height },
            wallBrick,
            { perpendGapMm: perpend, bedGapMm: BED_JOINT_GAP_MM },
          )) {
            return false;
          }
        }
      }
      return true;
    }

    // Slow path: off-lattice offsets, so rounding depends on the origin.
    // Check each member against the wall AND against a growing candidate.
    const candidate = new WallModel([], perpend);
    for (const entry of compiled.entries) {
      let x: number;
      let y: number;
      try {
        x = quantise(ox + entry.dx);
        y = quantise(oy + entry.dy);
      } catch {
        return false;
      }
      if (!this.canPlace(x, y, entry.width, entry.height, exclude)) return false;
      if (!candidate.canPlace(x, y, entry.width, entry.height)) return false;
      candidate.addBrick(entry.kind, x, y, {
        rotation: entry.rotation,
        materialId: entry.materialId,
        validate: false,
      });
    }
    return true;
  }

  /** Atomically add a copied pattern and return its freshly identified bricks. */
  placePattern(pattern: BrickPattern, originX: number, originY: number): PlacedBrick[] {
    const ox = quantise(originX);
    const oy = quantise(originY);
    if (!this.canPlacePattern(pattern, ox, oy)) {
      fail('Pattern overlaps the wall or has insufficient perpend/bed joint clearance');
    }
    return pattern.bricks.map((entry) =>
      this.addBrick(entry.kind, ox + entry.dx, oy + entry.dy, {
        rotation: entry.rotation,
        materialId: entry.materialId,
        validate: false,
      }),
    );
  }

  /**
   * Clause 8.5. Structurally identical to single-brick snapping, applied per
   * member with that member's offset subtracted from every target, so any
   * member can drive the snap. Datum candidates are aggregated across members,
   * which lets the leftmost brick meet the Y-axis while a different, lowest
   * brick meets the baseline.
   */
  snapPatternPosition(
    pattern: BrickPattern,
    rawX: number,
    rawY: number,
    toleranceMm: number,
    exclude?: ReadonlySet<BrickId> | null,
  ): SnapResult {
    const x = quantise(rawX);
    const y = quantise(rawY);
    const tolerance = Math.max(0, toleranceMm);
    const compiled = this.#compilePattern(pattern);
    if (compiled === null || compiled.entries.length === 0 || compiled.internallyValid === false) {
      return unsnapped(x, y);
    }

    const perpend = this.#perpendGapMm;
    const candidates = new SnapCandidateSet();
    let order = 0;
    const axisX = new Set<number>();
    const baselineY = new Set<number>();
    const paddingX = perpend + tolerance;
    const paddingY = BED_JOINT_GAP_MM + tolerance;

    const add = (
      cx: number,
      cy: number,
      priority: SnapPriority,
      relation: SnapRelation,
      referenceId: BrickId | null,
      snappedX: boolean,
      snappedY: boolean,
    ): void => {
      candidates.add({
        x: quantise(cx),
        y: quantise(cy),
        distance: Math.hypot(cx - x, cy - y),
        priority,
        order: order++,
        relation,
        referenceId,
        snappedX,
        snappedY,
      });
    };

    // Pre-bucket wall bricks per member when the fast path is available.
    let nearbyByEntry: Map<number, PlacedBrick[]> | null = null;
    if (compiled.relativeIndex !== null && compiled.bounds !== null) {
      nearbyByEntry = new Map();
      const b = compiled.bounds;
      const nearbyWall = this.#index.intersecting(
        x + b.minX - paddingX,
        y + b.minY - paddingY,
        x + b.maxX + paddingX,
        y + b.maxY + paddingY,
      );
      for (const wallBrick of nearbyWall) {
        if (exclude?.has(wallBrick.id)) continue;
        const members = compiled.relativeIndex.intersecting(
          wallBrick.x - x - paddingX,
          wallBrick.y - y - paddingY,
          wallBrick.right - x + paddingX,
          wallBrick.top - y + paddingY,
        );
        for (const member of members) {
          const list = nearbyByEntry.get(member.id - 1);
          if (list === undefined) nearbyByEntry.set(member.id - 1, [wallBrick]);
          else list.push(wallBrick);
        }
      }
    }

    for (let index = 0; index < compiled.entries.length; index++) {
      const entry = compiled.entries[index] as PatternBrick;
      const entryX = x + entry.dx;
      const entryY = y + entry.dy;
      const nearby =
        nearbyByEntry === null
          ? this.#index
              .intersecting(
                entryX - paddingX,
                entryY - paddingY,
                entryX + entry.width + paddingX,
                entryY + entry.height + paddingY,
              )
              .filter((brick) => !exclude?.has(brick.id))
          : nearbyByEntry.get(index) ?? [];

      for (const brick of nearby) {
        const sides: readonly (readonly [number, SnapRelation])[] = [
          [brick.right + perpend - entry.dx, 'right'],
          [brick.x - perpend - entry.width - entry.dx, 'left'],
        ];
        const verticalAlignments = [brick.y - entry.dy, brick.top - entry.height - entry.dy];
        for (const [targetX, relation] of sides) {
          if (Math.abs(x - targetX) > tolerance + EPSILON) continue;
          for (const targetY of verticalAlignments) {
            if (Math.abs(y - targetY) <= tolerance + EPSILON) {
              add(targetX, targetY, SnapPriority.EndJoint, relation, brick.id, true, true);
            }
          }
        }

        const overlapsHorizontally =
          entryX < brick.right - EPSILON && entryX + entry.width > brick.x + EPSILON;
        if (!overlapsHorizontally) continue;
        const beds: readonly (readonly [number, SnapRelation])[] = [
          [brick.top + BED_JOINT_GAP_MM - entry.dy, 'above'],
          [brick.y - BED_JOINT_GAP_MM - entry.height - entry.dy, 'below'],
        ];
        for (const [targetY, relation] of beds) {
          if (Math.abs(y - targetY) > tolerance + EPSILON) continue;
          const alignments = [
            brick.x - entry.dx,
            brick.right - entry.width - entry.dx,
            brick.centreX - entry.width / 2 - entry.dx,
            brick.centreX + perpend / 2 - entry.dx,
            brick.centreX - perpend / 2 - entry.width - entry.dx,
          ];
          const targetX = pickNearest(alignments, x);
          const snapX = Math.abs(targetX - x) <= tolerance * 0.6 + EPSILON;
          add(snapX ? targetX : x, targetY, SnapPriority.BedJoint, relation, brick.id, snapX, true);
        }
      }

      const baseline = -entry.dy;
      if (Math.abs(y - baseline) <= tolerance + EPSILON) baselineY.add(quantise(baseline));
      for (const targetX of [-entry.dx, -entry.dx - entry.width / 2, -entry.dx - entry.width]) {
        if (Math.abs(x - targetX) <= tolerance + EPSILON) axisX.add(quantise(targetX));
      }
    }

    if (compiled.bounds !== null) {
      const groupWidth = compiled.bounds.maxX - compiled.bounds.minX;
      const groupHeight = compiled.bounds.maxY - compiled.bounds.minY;
      for (const target of this.#sketchTargets(x, y, groupWidth, groupHeight, tolerance)) {
        add(target.x, target.y, SnapPriority.SketchLine, 'sketch', null, target.snappedX, target.snappedY);
      }
    }

    const sortedAxisX = [...axisX].sort((a, b) => a - b);
    const sortedBaselineY = [...baselineY].sort((a, b) => a - b);
    for (const targetX of sortedAxisX) {
      for (const targetY of sortedBaselineY) {
        add(targetX, targetY, SnapPriority.BothAxes, 'axes', null, true, true);
      }
    }
    for (const targetX of sortedAxisX) {
      add(targetX, y, SnapPriority.OneAxis, 'y-axis', null, true, false);
    }
    for (const targetY of sortedBaselineY) {
      add(x, targetY, SnapPriority.OneAxis, 'baseline', null, false, true);
    }

    for (const candidate of candidates.sorted()) {
      if (this.canPlacePattern(pattern, candidate.x, candidate.y, exclude)) {
        return {
          x: candidate.x,
          y: candidate.y,
          relation: candidate.relation,
          referenceId: candidate.referenceId,
          snappedX: candidate.snappedX,
          snappedY: candidate.snappedY,
        };
      }
    }
    return unsnapped(x, y);
  }

  // ---------- measurement ----------

  /**
   * Clause 9.1. Rank by distance from the point to the brick's RECTANGLE
   * first, then by corner distance — so the pointer picks the brick it is
   * over or nearest to, and only then the closest corner of that brick.
   */
  nearestCorner(x: number, y: number): CornerSnap | null {
    if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) {
      fail('Measurement coordinates must be finite numbers');
    }

    const closestFrom = (
      bricks: readonly PlacedBrick[],
    ): { key: readonly [number, number, number, number]; snap: CornerSnap } | null => {
      let bestKey: readonly [number, number, number, number] | null = null;
      let bestSnap: CornerSnap | null = null;
      for (const brick of bricks) {
        const dx = Math.max(brick.x - x, 0, x - brick.right);
        const dy = Math.max(brick.y - y, 0, y - brick.top);
        const rectDistanceSq = dx * dx + dy * dy;
        const corners: readonly (readonly [number, number])[] = [
          [brick.x, brick.y],
          [brick.right, brick.y],
          [brick.x, brick.top],
          [brick.right, brick.top],
        ];
        let cornerIndex = 0;
        let cornerDistanceSq = Infinity;
        for (let i = 0; i < corners.length; i++) {
          const [cx, cy] = corners[i] as readonly [number, number];
          const d = (cx - x) ** 2 + (cy - y) ** 2;
          if (d < cornerDistanceSq) {
            cornerDistanceSq = d;
            cornerIndex = i;
          }
        }
        const key = [rectDistanceSq, cornerDistanceSq, brick.id, cornerIndex] as const;
        if (bestKey === null || compareTuples(key, bestKey) < 0) {
          const [cx, cy] = corners[cornerIndex] as readonly [number, number];
          bestKey = key;
          bestSnap = {
            x: cx,
            y: cy,
            brickId: brick.id,
            corner: CORNER_NAMES[cornerIndex] as CornerSnap['corner'],
          };
        }
      }
      return bestKey === null || bestSnap === null ? null : { key: bestKey, snap: bestSnap };
    };

    let brickSnap: CornerSnap | null = null;
    if (this.#bricks.length > 0) {
      // Expand outward rather than scanning the wall. A brick outside the
      // search square is farther than `radius` on some axis, so it cannot beat
      // a result already within the radius.
      let radius = SPATIAL_CELL_SIZE_MM;
      for (let attempt = 0; attempt < 32 && brickSnap === null; attempt++) {
        const nearby = this.#index.intersecting(x - radius, y - radius, x + radius, y + radius);
        if (nearby.length > 0) {
          const best = closestFrom(nearby);
          if (best !== null && best.key[0] <= radius * radius + EPSILON) brickSnap = best.snap;
        }
        radius *= 2;
      }
      if (brickSnap === null) brickSnap = closestFrom(this.#bricks)?.snap ?? null;
    }

    // Sketch vertices are measurable too: once an outline exists, a window
    // reveal or a wall end is exactly what one wants to measure between.
    let vertexSnap: CornerSnap | null = null;
    let vertexDistanceSq = Infinity;
    for (const segment of this.#sketchSegments) {
      for (const p of [segment.a, segment.b]) {
        const d = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (d < vertexDistanceSq) {
          vertexDistanceSq = d;
          vertexSnap = { x: p.x, y: p.y, brickId: null, corner: 'sketch' };
        }
      }
    }

    if (brickSnap === null) return vertexSnap;
    if (vertexSnap === null) return brickSnap;
    // A drawn vertex only wins when it is strictly nearer the pointer than the
    // brick corner already chosen, so the clause 9.1 ranking is untouched
    // wherever the sketch is not the closer thing.
    const brickDistanceSq = (brickSnap.x - x) ** 2 + (brickSnap.y - y) ** 2;
    return vertexDistanceSq < brickDistanceSq ? vertexSnap : brickSnap;
  }

  // ---------- snapshots ----------

  snapshot(): WallSnapshot {
    return { perpendGapMm: this.#perpendGapMm, bricks: [...this.#bricks], sketch: this.#sketch };
  }

  /**
   * Restore an undo snapshot. Clearance is NOT revalidated: a snapshot can
   * only have come from a model that was already valid, and revalidating every
   * brick on every undo is pure cost (design document D5).
   */
  restoreSnapshot(snapshot: WallSnapshot): void {
    const restored = new WallModel([], snapshot.perpendGapMm);
    restored.setSketch(snapshot.sketch ?? EMPTY_SKETCH);
    for (const brick of snapshot.bricks) {
      if (restored.#index.has(brick.id)) fail(`Duplicate brick ID: ${brick.id}`);
      restored.#bricks.push(brick);
      restored.#index.add(brick);
      restored.#nextId = Math.max(restored.#nextId, brick.id + 1);
    }
    this.#adopt(restored);
  }

  /**
   * Clause 5.4. Rebuild from serialised records WITH full validation, then
   * adopt atomically — if any record is malformed or any brick fails
   * clearance, the live wall is left completely untouched.
   */
  restoreRecords(records: Iterable<unknown>, perpendGapMm: number = this.#perpendGapMm): void {
    const restored = new WallModel([], perpendGapMm);
    // The outline is not part of the brick list, so a records reload keeps it.
    restored.setSketch(this.#sketch);
    const required = ['id', 'kind', 'x', 'y', 'rotation', 'materialId'] as const;
    for (const entry of records) {
      if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
        fail('Each brick record must be an object');
      }
      const record = entry as Record<string, unknown>;
      const missing = required.filter((key) => !Object.hasOwn(record, key));
      if (missing.length > 0) fail(`Brick record is missing: ${[...missing].sort().join(', ')}`);
      const { x, y } = record;
      if (typeof x !== 'number' || typeof y !== 'number') fail('Brick coordinates must be numbers');
      if (!Number.isFinite(x) || !Number.isFinite(y)) fail('Brick coordinates must be finite');
      restored.addBrick(String(record['kind']), x, y, {
        rotation: record['rotation'] as number,
        materialId: record['materialId'] as MaterialId,
        brickId: record['id'] as BrickId,
        validate: true,
      });
    }
    this.#adopt(restored);
  }

  /** Serialise to plain records, in list order. */
  toRecords(): BrickRecord[] {
    return this.#bricks.map((brick) => ({
      id: brick.id,
      kind: brick.kind,
      x: brick.x,
      y: brick.y,
      rotation: brick.rotation,
      materialId: brick.materialId,
    }));
  }

  // ---------- internals shared with the perpend solver ----------

  /** @internal Used by `perpend-solver.ts`, which is part of this module. */
  _internals() {
    return {
      index: this.#index,
      setPerpendGap: (value: Mm): void => {
        this.#perpendGapMm = value;
        this.#touch();
      },
      adopt: (other: WallModel): void => this.#adopt(other),
      nextId: (): number => this.#nextId,
      setNextId: (value: number): void => {
        this.#nextId = value;
      },
    };
  }
}

function pickNearest(values: readonly number[], target: number): number {
  let best = values[0] as number;
  let bestDistance = Math.abs(best - target);
  for (let i = 1; i < values.length; i++) {
    const value = values[i] as number;
    const distance = Math.abs(value - target);
    // Ties resolve to the smaller value, matching the reference sort key.
    if (distance < bestDistance || (distance === bestDistance && value < best)) {
      best = value;
      bestDistance = distance;
    }
  }
  return best;
}

function compareTuples(
  a: readonly [number, number, number, number],
  b: readonly [number, number, number, number],
): number {
  for (let i = 0; i < a.length; i++) {
    const av = a[i] as number;
    const bv = b[i] as number;
    if (av !== bv) return av < bv ? -1 : 1;
  }
  return 0;
}

export { brickSpec };
